import type { ReactNode } from 'react';
import { Card } from './card';
import { InitialsAvatar } from './initials-avatar';

interface EntityCardProps {
	name: string;
	subtitle?: string;
	avatar?: string | null;
	/** Number of initials to show (default 2) */
	maxInitials?: number;
	/** Badge or status indicator rendered top-right of header */
	badge?: ReactNode;
	/** Action control (e.g. dropdown menu) rendered top-right after the badge */
	actions?: ReactNode;
	/** Content between header and metrics (e.g. description, team list) */
	children?: ReactNode;
	/** Bottom metrics grid items */
	metrics?: Array<{ label: string; value: ReactNode }>;
	/** Grid columns for metrics (default: metrics.length or 2) */
	metricColumns?: number;
	onClick?: () => void;
}

export function EntityCard({
	name,
	subtitle,
	avatar,
	maxInitials = 2,
	badge,
	actions,
	children,
	metrics,
	metricColumns,
	onClick,
}: EntityCardProps) {
	const cols = metricColumns ?? metrics?.length ?? 2;

	return (
		<Card
			className="cursor-pointer border-line-1 bg-surface gap-0 py-0 transition-[border-color] duration-[180ms] hover:border-line-2"
			onClick={onClick}
		>
			<div className="p-4">
				{/* Header */}
				<div
					className="flex items-start gap-3"
					style={{ marginBottom: children || metrics ? 14 : 0 }}
				>
					<InitialsAvatar
						name={name}
						src={avatar}
						size={44}
						shape="rounded"
						maxInitials={maxInitials}
					/>
					<div className="min-w-0 flex-1">
						<div className="mb-0.5 truncate text-base font-medium text-text-1">
							{name}
						</div>
						{subtitle && <div className="truncate text-sm text-text-3">{subtitle}</div>}
					</div>
					{badge}
					{actions}
				</div>

				{/* Optional middle content */}
				{children}

				{/* Metrics grid */}
				{metrics && metrics.length > 0 && (
					<div
						className="gap-3 pt-3.5"
						style={{
							display: 'grid',
							gridTemplateColumns: `repeat(${cols}, 1fr)`,
							borderTop: '0.5px solid var(--line-1)',
						}}
					>
						{metrics.map((m) => (
							<div key={m.label}>
								<div className="mb-1 text-2xs font-medium uppercase tracking-[0.06em] text-text-4">
									{m.label}
								</div>
								<div className="truncate text-sm text-text-2">{m.value}</div>
							</div>
						))}
					</div>
				)}
			</div>
		</Card>
	);
}
